import { useState } from 'react';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { useNavigate, generatePath } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { deserialize } from 'serializr';
import { ProductRawMaterial } from 'models/ProductRawMaterial/productRawMaterial.model';
import { ProductRawMaterialTransportations } from 'models/ProductRawMaterialTransportations/productRawMaterialTransportations.model';
import { TotalCarbonEmissionProducts } from 'models/TotalCarbonEmissionProducts/totalCarbonEmissionProduct.model';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/NotificationEnums';
import { ApiMessage } from 'views/CreateProductFlow/ProductOverview/successMessage';
import { MetaData } from 'models/MetaData/metaData.model';
import { ProductDistributionEmissionData } from 'models/ProductDistributionEmissionData/ProductDistributionEmissionData.model';
import { ProcessingFacilityEmissionData } from 'models/ProcessingFacilityEmissionData/ProcessingFacilityEmissionData.model';

const EmissionService = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [stage1Emissions, setStage1Emissions] = useState<ProductRawMaterial[]>();
  const [stage1EmissionsTotal, setStage1EmissionsTotal] = useState<number>();
  const [stage2Emissions, setStage2Emissions] = useState<ProductRawMaterialTransportations[]>();
  const [stage2EmissionsTotal, setStage2EmissionsTotal] = useState<number>();
  const [stage3Emissions, setStage3Emissions] = useState<ProcessingFacilityEmissionData>();
  const [stage3EmissionsTotal, setStage3EmissionsTotal] = useState<number>();
  const [stage4Emissions, setStage4Emissions] = useState<ProductDistributionEmissionData[]>();
  const [stage4EmissionsTotal, setStage4EmissionsTotal] = useState<number>();
  const [totalEmissionProducts, setTotalEmissionsProducts] = useState<TotalCarbonEmissionProducts>();
  const [totalEmissionAnnualProduction, setTotalEmissionAnnualProduction] = useState<string>();
  const [referralData, setReferralData] = useState<MetaData[]>([]);
  const [companyTypeMeta, setCompanyTypeMeta] = useState<MetaData[]>([]);
  const [metaData, setMetaData] = useState<MetaData[]>([]);
  /** Product Emission API: Stage 1 */
  const fetchProductEmissionsStage1 = async (id: string | undefined) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FETCH_STAGE1_EMISSIONS, { id });
      const response = await axiosInstance.get(url);

      const data = response.data['rawMaterialEmissionData'];
      setStage1Emissions(
        deserialize(ProductRawMaterial, data as ProductRawMaterial[])
      );
      const total = response.data['total'];
      setStage1EmissionsTotal(total);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchProductEmissionsStage1AndStage2 = async (id?: string) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FETCH_STAGE1_AND_STAGE2, { id });
      const response = await axiosInstance.get(url);
      const rawMaterialData = deserialize(
        ProductRawMaterial,
        response.data.rawMaterial[
        'rawMaterialEmissionData'
        ] as ProductRawMaterial[]
      );
      const transportationData = deserialize(
        ProductRawMaterialTransportations,
        response.data.transport[
        'transportEmissionData'
        ] as ProductRawMaterialTransportations[]
      );
      setStage1Emissions(rawMaterialData);
      setStage2Emissions(transportationData);
      setStage1EmissionsTotal(response.data.rawMaterial['total']);
      setStage2EmissionsTotal(response.data.transport['total']);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchProductEmissionsStage2 = async (id: string | undefined) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FETCH_STAGE2_EMISSIONS, { id });
      const response = await axiosInstance.get(url);

      const data = response.data['transportEmissionData'];
      setStage2Emissions(
        deserialize(
          ProductRawMaterialTransportations,
          data as ProductRawMaterialTransportations[]
        )
      );
      const total = response.data['total'];
      setStage2EmissionsTotal(total);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchProductEmissionsStage3 = async (id: string | undefined) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FETCH_STAGE3_EMISSIONS, { id });
      const response = await axiosInstance.get(url);

      const data = deserialize(ProcessingFacilityEmissionData, response.data as ProcessingFacilityEmissionData);
      setStage3Emissions(data);
      const total = response.data['co2e'];
      setStage3EmissionsTotal(total);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchProductEmissionsStage4 = async (id: string | undefined) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FETCH_STAGE4_EMISSIONS, { id });
      const response = await axiosInstance.get(url);

      const data = deserialize(ProductDistributionEmissionData, response.data['distributionEmissionData'] as ProductDistributionEmissionData[]);
      setStage4Emissions(data);
      const total = response.data['total'];
      setStage4EmissionsTotal(total);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** Update Product Status */
  const UpdateProductStatus = async (
    id: string | undefined,
    payload: { status: string }
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.UPDATE_PRODUCT_STATUS, { id });
      const response = await axiosInstance.put(url, payload);
      const errorMessage = response.data?.error?.message;
      if (errorMessage && errorMessage.length > 0) {
        return errorMessage;
      }
      Notification({
        message: ApiMessage.SENT_FOR_APPROVAL,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      navigate(`${AppRoutes.PRODUCT}/${AppRoutes.IN_REVIEW}`);
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /** fetch total emission of products */
  const fetchTotalEmissionProducts = async () => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FETCH_EMISSION_ALLPRODUCTS);
      const response = await axiosInstance.get(url);
      const data = deserialize(
        TotalCarbonEmissionProducts,
        response.data
      ) as TotalCarbonEmissionProducts;
      setTotalEmissionsProducts(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /* fetchTotalEmissionOfAnnualProduction */
  const fetchTotalEmissionAnnualProduction = async (
    id?: number,
    params?: {
      business_model?: string;
    }
  ) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FETCH_EMISSION_ANNUAL_PRODUCTION, {
        id,
      });
      const response = await axiosInstance.get(url, { params });
      const data = Number(response.data['total_co2e']).toFixed(2);
      setTotalEmissionAnnualProduction(data);
    } catch (error) {
    } finally {
    }
  };

  // Fetch Metadata for: Revenue_range, referral, brand_categories
  const fetchReferralData = async (params?: { metadata_type?: string }) => {
    try {
      const res = await axiosInstance.get(ApiRoutes.FETCH_META_DATA, {
        params,
      });
      const desiredMetaData = deserialize(
        MetaData,
        res.data.data.metadata
      ) as MetaData[];
      setReferralData(desiredMetaData);
    } catch (error) {
    } finally {
    }
  };

  // Fetch Metadata for: Revenue_range, referral, brand_categories
  const fetchCompanyTypeMeta = async (params?: { metadata_type?: string }) => {
    try {
      const res = await axiosInstance.get(ApiRoutes.FETCH_META_DATA, {
        params,
      });
      const desiredMetaData = deserialize(
        MetaData,
        res.data.data.metadata
      ) as MetaData[];

      setCompanyTypeMeta(desiredMetaData);
    } catch (error) {
    } finally {
    }
  };

  // Fetch Metadata for: Revenue_range, referral, brand_categories
  const fetchMetaData = async (params?: { metadata_type?: string }) => {
    try {
      const res = await axiosInstance.get(ApiRoutes.FETCH_META_DATA, {
        params,
      });
      const desiredMetaData = deserialize(
        MetaData,
        res.data.data.metadata
      ) as MetaData[];
      setMetaData(desiredMetaData);
    } catch (error) {
    } finally {
    }
  };

  const checkTotalEmissionStatus = async () => {
    try {
      await axiosInstance.get(ApiRoutes.TOTAL_EMISSION_CHECK);
    } catch (error) {
    } finally {
    }
  };

  return {
    loading,
    buttonLoading,
    stage1Emissions,
    stage2Emissions,
    stage3Emissions,
    stage4Emissions,
    stage1EmissionsTotal,
    stage2EmissionsTotal,
    stage3EmissionsTotal,
    stage4EmissionsTotal,
    totalEmissionProducts,
    totalEmissionAnnualProduction,
    fetchProductEmissionsStage1,
    fetchProductEmissionsStage1AndStage2,
    fetchProductEmissionsStage2,
    fetchProductEmissionsStage3,
    fetchProductEmissionsStage4,
    UpdateProductStatus,
    fetchTotalEmissionProducts,
    fetchTotalEmissionAnnualProduction,
    referralData,
    companyTypeMeta,
    fetchCompanyTypeMeta,
    fetchReferralData,
    fetchMetaData,
    metaData,
    checkTotalEmissionStatus
  };
};

export default EmissionService; 